// Pure game logic for Number Placement Classic — kept separate from the UI.

export const cellKey = (r, c) => `${r}-${c}`;

// cellKey -> [{ slotId, pos }]
export const buildCellIndex = (slots) => {
  const idx = {};
  slots.forEach((s) => {
    s.cells.forEach((cell, i) => {
      const k = cellKey(cell[0], cell[1]);
      (idx[k] = idx[k] || []).push({ slotId: s.id, pos: i });
    });
  });
  return idx;
};

export const mapById = (arr) => {
  const m = {};
  arr.forEach((x) => { m[x.id] = x; });
  return m;
};

// Which categorized slots (given / hint / user) cover a cell, with correctness.
export const computeCellState = ({ r, c, cellIndex, placements, blocksById, solution, givenSlotIds, hintSlotIds }) => {
  const k = cellKey(r, c);
  const covers = cellIndex[k] || [];
  const placedCovers = covers.filter((cv) => placements[cv.slotId]);
  if (placedCovers.length === 0) return { filled: false, digit: "", state: "empty" };

  let digit = "";
  let hasError = false;
  let hasUser = false;
  let hasHint = false;
  let hasGiven = false;

  placedCovers.forEach(({ slotId, pos }) => {
    const bid = placements[slotId];
    const val = blocksById[bid].value;
    if (!digit) digit = val[pos];
    const correct = val === solution[slotId];
    if (!correct) hasError = true;
    else if (givenSlotIds.has(slotId)) hasGiven = true;
    else if (hintSlotIds.has(slotId)) hasHint = true;
    else hasUser = true;
    // detect conflicting digit across two placed slots
    if (val[pos] !== digit) hasError = true;
  });

  let state = "given";
  if (hasError) state = "error";
  else if (hasUser) state = "correct";
  else if (hasHint) state = "hint";
  else if (hasGiven) state = "given";
  return { filled: true, digit, state };
};

// Valid empty slots for a block of the given length.
export const validTargetSlots = (slots, placements, length) =>
  slots.filter((s) => s.length === length && !placements[s.id]).map((s) => s.id);

// Choose which slot to place into when a cell is clicked.
export const chooseSlotForCell = (r, c, cellIndex, validSet) => {
  const k = cellKey(r, c);
  const covers = cellIndex[k] || [];
  const candidates = covers.filter((cv) => validSet.has(cv.slotId));
  if (candidates.length === 0) return null;
  // prefer the slot where this cell is the start (pos 0)
  const startCand = candidates.find((cv) => cv.pos === 0);
  return (startCand || candidates[0]).slotId;
};

// Choose a placed, removable slot when a filled cell is clicked.
export const choosePlacedSlotForCell = (r, c, cellIndex, placements, lockedSet) => {
  const k = cellKey(r, c);
  const covers = cellIndex[k] || [];
  const placed = covers.filter((cv) => placements[cv.slotId] && !lockedSet.has(cv.slotId));
  if (placed.length === 0) return null;
  const startCand = placed.find((cv) => cv.pos === 0);
  return (startCand || placed[0]).slotId;
};

export const isSlotCorrect = (slotId, placements, blocksById, solution) => {
  const bid = placements[slotId];
  if (!bid) return false;
  return blocksById[bid].value === solution[slotId];
};

export const isWin = (slots, placements, blocksById, solution) =>
  slots.every((s) => isSlotCorrect(s.id, placements, blocksById, solution));

export const formatTime = (ms) => {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};
